import {useRef, useState, type ComponentProps, type MouseEvent, type ReactNode} from 'react';
import {Eye, EyeOff, Palette, SwatchBook} from 'lucide-react';
import {
  Button,
  ButtonGroup,
  ToggleGroup,
  ToggleGroupItem
} from '@kroll-resolver-crisp/ui-components-react';
import {Thumbnail} from '@/components/shared/Thumbnail';
import {FILTER_BAR_COMPACT_LABEL} from '@/lib/filterBar';
import {cn} from '@/lib/utils';
import {useUiStore} from '@/store/uiStore';

// Hidden only where hover exists to reveal it, so a touch screen (no hover) always shows the
// controls. Relies on an ancestor carrying the unnamed `group` class, as every card here already
// does. Keyboard focus keeps them up (`:focus-visible`), but a mouse click's focus does not -
// `group-focus-within` would pin them open after the pointer had left.
export const SENSITIVE_CONTROLS_VISIBILITY =
  'transition-opacity [@media(hover:hover)]:opacity-0 [@media(hover:hover)]:group-hover:opacity-100 [@media(hover:hover)]:group-has-focus-visible:opacity-100';

// Slight scale hides the soft edge a blur leaves; only while the blur class is applied, so a
// revealed image sits at its natural size.
export const BLUR_IMAGE_CLASS = '[&.blur-lg]:scale-105 [&.blur-lg]:blur-[10px]';

/** One filter (blur or greyscale) as an inline control sees it. */
export type SensitiveFilterControl = {
  // Whether the filter is currently on this media; the button offers to remove it, or to put it back.
  readonly applied: boolean;
  readonly onToggle: () => void;
};

export type SensitiveMediaControlsState = {
  // Both filters are always on offer, whatever the page preferences say: they set the starting point,
  // and the analyst can apply or remove either on any one piece of media.
  readonly blur: SensitiveFilterControl;
  readonly greyscale: SensitiveFilterControl;
};

/**
The reveal state behind one piece of sensitive media plus its inline controls. `thumbnailProps` go on
the `Thumbnail`, `controls` on `SensitiveMediaControls`; split so a card can place the controls
inside its own overlay layer (see ItemCard) rather than where `SensitiveMedia` would put them.
*/
export function useSensitiveMedia(): {
  thumbnailProps: Pick<
    ComponentProps<typeof Thumbnail>,
    'toggleBlurRef' | 'onBlurredChange' | 'toggleGreyscaleRef' | 'onGreyedChange'
  >;
  controls: SensitiveMediaControlsState;
} {
  const blurThumbnails = useUiStore((s) => s.blurThumbnails);
  const greyscaleThumbnails = useUiStore((s) => s.greyscaleThumbnails);
  // Mirror the Thumbnail's own state, which already folds each page-level preference together
  // with this image's own override.
  const [blurred, setBlurred] = useState(blurThumbnails);
  const [greyed, setGreyed] = useState(greyscaleThumbnails);
  const toggleBlurRef = useRef<(() => void) | null>(null);
  const toggleGreyscaleRef = useRef<(() => void) | null>(null);

  return {
    thumbnailProps: {
      toggleBlurRef,
      onBlurredChange: setBlurred,
      toggleGreyscaleRef,
      onGreyedChange: setGreyed
    },
    controls: {
      blur: {
        applied: blurred,
        onToggle() {
          toggleBlurRef.current?.();
        }
      },
      greyscale: {
        applied: greyed,
        onToggle() {
          toggleGreyscaleRef.current?.();
        }
      }
    }
  };
}

// One icon per filter state, shared by the inline controls and the page toggle so the two read as the
// same thing. `applied` is the filter being on; the icon is the one for the action that undoes it.
export function BlurIcon({applied}: {readonly applied: boolean}) {
  return applied ? <Eye /> : <EyeOff />;
}

export function GreyscaleIcon({applied}: {readonly applied: boolean}) {
  return applied ? <Palette /> : <SwatchBook />;
}

// The verbs. Blur and Unblur are a pair; Greyscale and Colourise are the other. A button always
// names what clicking it does.
function blurLabel(applied: boolean) {
  return applied ? 'Unblur' : 'Blur';
}

function greyscaleLabel(applied: boolean) {
  return applied ? 'Colourise' : 'Greyscale';
}

/**
The Blur/Unblur and Greyscale/Colourise buttons, always both, so either can be applied or removed
whatever the page preferences. Never lets a click reach the card or button underneath. `compact`
renders only icons (labelled for assistive tech), for media too small to carry text. `overMedia`
gives them the dark style that reads over an image; otherwise they are ordinary outline buttons.
*/
function SensitiveMediaButtons({
  blur,
  greyscale,
  compact,
  overMedia,
  className
}: SensitiveMediaControlsState & {
  readonly compact: boolean;
  readonly overMedia: boolean;
  readonly className?: string;
}) {
  const buttons = [
    {
      label: blurLabel(blur.applied),
      icon: <BlurIcon applied={blur.applied} />,
      onClick: blur.onToggle
    },
    {
      label: greyscaleLabel(greyscale.applied),
      icon: <GreyscaleIcon applied={greyscale.applied} />,
      onClick: greyscale.onToggle
    }
  ];

  return (
    <ButtonGroup className={className} aria-label="Sensitive media">
      {buttons.map(({label, icon, onClick}) => (
        <Button
          key={label}
          size={compact ? 'icon-sm' : 'sm'}
          variant={overMedia ? 'default' : 'outline'}
          aria-label={compact ? label : undefined}
          title={compact ? label : undefined}
          className={cn(
            overMedia && 'bg-black/70 text-white hover:bg-black/85',
            'pointer-coarse:min-h-11'
          )}
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.preventDefault();
            event.stopPropagation();
            // A pointer click leaves focus on the button, which would keep the controls showing
            // after the pointer leaves; a keyboard press (detail 0) keeps its focus.
            if (event.detail > 0) {
              event.currentTarget.blur();
            }

            onClick();
          }}>
          {icon}
          {compact ? null : label}
        </Button>
      ))}
    </ButtonGroup>
  );
}

/**
The inline controls over one piece of media (see `SensitiveMediaButtons`), centred over it.
*/
export function SensitiveMediaControls({
  compact = false,
  className,
  ...controls
}: SensitiveMediaControlsState & {
  readonly compact?: boolean;
  readonly className?: string | undefined;
}) {
  // Centred with a flex wrapper, not a translate on the button: Button's own
  // `active:translate-y-px` replaces `-translate-y-1/2`, so a translate-centred
  // button jumped down on mouse-down.
  return (
    <div
      className={cn(
        'pointer-events-none absolute inset-0 z-20 flex items-center justify-center',
        className
      )}>
      <SensitiveMediaButtons
        {...controls}
        overMedia
        compact={compact}
        className="pointer-events-auto"
      />
    </div>
  );
}

/**
Sensitive platform media with the inline controls over it - the one behaviour for every card showing
platform media, actor avatars included. Render it inside a positioned container carrying the
`group` class; the controls show only while the pointer is over it (or on keyboard focus), and
always on touch. `compact` shows just icons, for small media such as list-row thumbnails.
*/
export function SensitiveMedia({
  className,
  compact = false,
  ...props
}: Omit<
  ComponentProps<typeof Thumbnail>,
  'toggleBlurRef' | 'onBlurredChange' | 'toggleGreyscaleRef' | 'onGreyedChange'
> & {
  readonly compact?: boolean;
}) {
  const {thumbnailProps, controls} = useSensitiveMedia();

  return (
    <>
      <Thumbnail {...props} {...thumbnailProps} className={cn(BLUR_IMAGE_CLASS, className)} />
      <SensitiveMediaControls
        {...controls}
        compact={compact}
        className={SENSITIVE_CONTROLS_VISIBILITY}
      />
    </>
  );
}

/**
Sensitive media that is itself the target of an action (open a review, view the full image). The
controls sit beside the action button rather than inside it, because a button can't nest another.
`children` render over the image inside the button (a hover scrim, say). Give it a size through
`className`; the button fills it.
*/
export function SensitiveMediaButton({
  label,
  onClick,
  compact = false,
  className,
  imageClassName,
  children,
  ...props
}: Omit<
  ComponentProps<typeof Thumbnail>,
  | 'toggleBlurRef'
  | 'onBlurredChange'
  | 'toggleGreyscaleRef'
  | 'onGreyedChange'
  | 'onClick'
  | 'className'
> & {
  readonly label: string;
  // Told whether the image was blurred, and whether greyscaled, at click time, so an "expand" action
  // can open it as the analyst last saw it rather than applying either again.
  readonly onClick: (blurred: boolean, greyed: boolean) => void;
  readonly compact?: boolean;
  readonly className?: string;
  readonly imageClassName?: string;
  readonly children?: ReactNode;
}) {
  const {thumbnailProps, controls} = useSensitiveMedia();

  return (
    <div className={cn('group relative', className)}>
      <button
        type="button"
        aria-label={label}
        className="relative block size-full"
        onClick={() => {
          onClick(controls.blur.applied, controls.greyscale.applied);
        }}>
        <Thumbnail
          {...props}
          {...thumbnailProps}
          className={cn(BLUR_IMAGE_CLASS, imageClassName)}
        />
        {children}
      </button>
      <SensitiveMediaControls
        {...controls}
        compact={compact}
        className={SENSITIVE_CONTROLS_VISIBILITY}
      />
    </div>
  );
}

/**
The analyst's sensitive-media preferences as one page control: [Blur | Greyscale]. Each half is its own
on/off preference, labelled with the same verb the inline controls show (Unblur while blur is on). It is per page rather than global: a page that shows platform media renders it
with its view controls, so it sits beside that page's own options and is absent from pages with
nothing to protect.
*/
export function SensitiveMediaToggle() {
  const blur = useUiStore((state) => state.blurThumbnails);
  const setBlur = useUiStore((state) => state.setBlurThumbnails);
  const greyscale = useUiStore((state) => state.greyscaleThumbnails);
  const setGreyscale = useUiStore((state) => state.setGreyscaleThumbnails);

  return (
    <ToggleGroup
      multiple
      variant="outline"
      spacing={0}
      size="sm"
      aria-label="Sensitive media"
      value={[...(blur ? ['blur'] : []), ...(greyscale ? ['greyscale'] : [])]}
      onValueChange={(next) => {
        setBlur(next.includes('blur'));
        setGreyscale(next.includes('greyscale'));
      }}>
      <ToggleGroupItem value="blur" aria-label={blurLabel(blur)} title={blurLabel(blur)}>
        <BlurIcon applied={blur} />
        <span className={FILTER_BAR_COMPACT_LABEL}>{blurLabel(blur)}</span>
      </ToggleGroupItem>
      <ToggleGroupItem
        value="greyscale"
        aria-label={greyscaleLabel(greyscale)}
        title={greyscaleLabel(greyscale)}>
        <GreyscaleIcon applied={greyscale} />
        <span className={FILTER_BAR_COMPACT_LABEL}>{greyscaleLabel(greyscale)}</span>
      </ToggleGroupItem>
    </ToggleGroup>
  );
}
